<template>
  <div class="multi-clipper-layout">
    <!-- Toolbar for Panel Toggles -->
    <div class="layout-toolbar">
      <div class="toolbar-left">
        <button 
          v-if="!showLeftPanel" 
          class="toggle-btn" 
          @click="showLeftPanel = true"
          title="显示搜索栏"
        >
          <i class="mdi mdi-arrow-expand-right"></i> 显示搜索
        </button>
      </div>
      
      <div class="toolbar-center">
         <!-- Help button moved to VideoPlayer.vue -->
      </div>

      <div class="toolbar-right">
        <button 
          v-if="!showRightPanel" 
          class="toggle-btn" 
          @click="showRightPanel = true"
          title="显示剪辑池"
        >
          <i class="mdi mdi-arrow-expand-left"></i> 显示剪辑池
        </button>
      </div>
    </div>

    <!-- Help Modal moved to VideoPlayer.vue -->

    <splitpanes class="default-theme" style="height: calc(100% - 30px)" :key="layoutKey">
      <!-- Left Panel -->
      <pane v-if="showLeftPanel" :min-size="10" :size="25" :max-size="100">
        <div class="panel left-panel">
          <div class="panel-header" style="display: flex; justify-content: space-between; align-items: center; width: 100%;">
             <div style="display: flex; align-items: center; gap: 8px;">
               <span class="panel-title">素材搜索</span>
               <button class="help-btn-small" style="background: none; border: none; color: #999; cursor: pointer; padding: 0; display: flex; align-items: center;" @click="materialSearchRef && (materialSearchRef.showHelp = true)" title="使用帮助">
                 <i class="mdi mdi-help-circle-outline"></i>
               </button>
               <v-btn
                 v-if="materialSearchRef && ['commodity', 'commodity_group'].includes(materialSearchRef.searchMode)"
                 size="x-small"
                 variant="tonal"
                 color="primary"
                 prepend-icon="mdi-crosshairs-gps"
                 @click="materialSearchRef.locateCurrentPlaying(true)"
                 title="定位到当前播放的商品片段"
               >
                 定位播放
               </v-btn>
             </div>
             <button class="icon-btn" @click.stop="showLeftPanel = false" title="隐藏">
               <i class="mdi mdi-chevron-left"></i>
             </button>
          </div>
          <MaterialSearch ref="materialSearchRef" :current-time="videoCurrentTime" />
        </div>
      </pane>
      
      <!-- Center Panel -->
      <pane :min-size="20" :max-size="100">
        <div class="panel center-panel">
          <splitpanes horizontal class="default-theme" style="height: 100%">
            <!-- Video Section -->
            <pane :min-size="10" :size="40">
              <div class="video-section">
                <VideoPlayer 
                  ref="videoPlayerRef"
                  :src="videoUrl"
                  :seek-request="seekRequest"
                  @timeupdate="onTimeUpdate"
                  @play="isPlaying = true"
                  @pause="isPlaying = false"
                  @time-update="onTimeUpdate"
                />
              </div>
            </pane>
            
            <!-- Source Section -->
            <pane :min-size="10" :size="60">
              <div 
                class="source-section" 
                :class="{ 'active-panel': activePanel === 'source' }"
                @click="activatePanel('source')"
              >
                <SourceViewer 
                  :current-time="videoCurrentTime"
                  @request-seek="handleSeekRequest"
                />
              </div>
            </pane>
          </splitpanes>
        </div>
      </pane>
      
      <!-- Right Panel -->
      <pane v-if="showRightPanel" :min-size="10" :size="25" :max-size="100">
        <div 
          class="panel right-panel"
          :class="{ 'active-panel': activePanel === 'pool' }"
          @click="activatePanel('pool')"
        >
          <div class="panel-header">
             <div style="display: flex; align-items: center; gap: 4px;">
               <span class="panel-title">剪辑池</span>
               <i class="mdi mdi-help-circle-outline help-icon-trigger" 
                  title="查看帮助"
                  @click.stop="showClipPoolHelp"></i>
             </div>
             <button class="icon-btn" @click.stop="showRightPanel = false" title="隐藏">
               <i class="mdi mdi-chevron-right"></i>
             </button>
          </div>
          <ClipPool 
            ref="clipPoolRef"
            :current-time="videoCurrentTime"
            :is-playing="isPlaying"
            @play-segment="handlePlaySegment"
            @request-seek="handleSeekRequest"
            @request-pause="handlePauseRequest"
          />
        </div>
      </pane>
    </splitpanes>

    <!-- 剪辑草稿恢复提示 -->
    <v-dialog v-model="showDraftDialog" max-width="560" persistent>
      <v-card>
        <v-card-title class="text-subtitle-1 font-weight-bold">发现剪辑草稿</v-card-title>
        <v-card-text>
          <div style="margin-bottom: 8px;">检测到以下未完成的剪辑草稿，是否恢复？</div>
          <v-list density="compact" style="background: transparent;">
            <v-list-item v-for="d in drafts" :key="d.draftKey">
              <div style="width: 100%;">
                <div style="font-weight: 600; font-size: 14px;">{{ d.summary.count }} 个片段 · {{ d.summary.durationText }} · {{ d.summary.time }}</div>
                <div v-if="d.summary.talents.length" style="font-size: 12px; color: #666; margin-top: 2px;">达人：{{ d.summary.talents.slice(0, 3).join('、') }}<span v-if="d.summary.talents.length > 3"> 等 {{ d.summary.talents.length }} 人</span></div>
                <div v-if="d.summary.products.length" style="font-size: 12px; color: #666; margin-top: 2px;">商品：{{ d.summary.products.slice(0, 3).join('、') }}<span v-if="d.summary.products.length > 3"> 等 {{ d.summary.products.length }} 个</span></div>
                <div v-if="d.summary.snippet" style="font-size: 12px; color: #999; margin-top: 2px;">开头：{{ d.summary.snippet }}</div>
              </div>
              <template v-slot:append>
                <v-btn size="small" color="primary" variant="tonal" @click="handleRestoreDraft(d)">恢复</v-btn>
                <v-btn size="small" color="error" variant="text" @click="handleDiscardDraft(d.draftKey)">丢弃</v-btn>
              </template>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-btn variant="text" color="error" @click="handleDiscardAllDrafts">全部丢弃</v-btn>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showDraftDialog = false">稍后再说</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { Splitpanes, Pane } from 'splitpanes'
import 'splitpanes/dist/splitpanes.css'
import MaterialSearch from './MaterialSearch.vue'
import SourceViewer from './SourceViewer.vue'
import ClipPool from './ClipPool.vue'
import VideoPlayer from './VideoPlayer.vue'
import { listDrafts, clearDraft } from '@/utils/poolDraftStorage'

export default {
  name: 'MultiClipperLayout',
  components: {
    Splitpanes,
    Pane,
    MaterialSearch,
    SourceViewer,
    ClipPool,
    VideoPlayer
  },
  setup() {
    const store = useStore()
    const videoCurrentTime = ref(0)
    const seekRequest = ref(null)
    const videoPlayerRef = ref(null)
    const clipPoolRef = ref(null)
    const materialSearchRef = ref(null)
    const isPlaying = ref(false)

    // 剪辑草稿恢复
    const showDraftDialog = ref(false)
    const drafts = ref([])

    const truncateText = (text, maxLen) => {
      const t = String(text || '').replace(/\s+/g, '')
      return t.length > maxLen ? t.slice(0, maxLen) + '…' : t
    }
    const segmentDuration = (s) => {
      const start = Number(s.start_time)
      const end = Number(s.end_time)
      return Number.isFinite(start) && Number.isFinite(end) && end > start ? end - start : 0
    }
    const formatDuration = (ms) => {
      const totalSec = Math.round(ms / 1000)
      const h = Math.floor(totalSec / 3600)
      const m = Math.floor((totalSec % 3600) / 60)
      const s = totalSec % 60
      if (h > 0) return `${h}小时${m}分${s}秒`
      if (m > 0) return `${m}分${s}秒`
      return `${s}秒`
    }
    const segTalent = (s) => s['达人昵称'] || s.talent_nickname || s.author || s.author_name || ''
    const segProduct = (s) => s.commodity_name || s['商品名称'] || s.product_name || ''
    const uniq = (arr) => [...new Set(arr.filter(Boolean))]
    // 汇总草稿信息：片段数、总时长、达人、商品、开头文字、保存时间
    const draftSummary = (d) => {
      const segs = (d.groups || []).flatMap(g => g.segments || [])
      const totalMs = segs.reduce((acc, s) => acc + segmentDuration(s), 0)
      const talents = uniq(segs.map(segTalent))
      const products = uniq(segs.map(segProduct))
      const first = segs.find(s => s.text || s.origin_text)
      const mins = Math.max(1, Math.floor((Date.now() - (d.savedAt || Date.now())) / 60000))
      return {
        count: segs.length,
        time: mins < 60 ? `${mins} 分钟前` : `${Math.floor(mins / 60)} 小时前`,
        durationText: formatDuration(totalMs),
        talents,
        products,
        snippet: first ? truncateText(first.text || first.origin_text, 40) : ''
      }
    }

    const handleRestoreDraft = (draft) => {
      // 删除旧草稿，避免恢复后出现两份；恢复后的状态会由自动保存写入当前标签页
      clearDraft(draft.draftKey)
      store.dispatch('multiClipper/restoreDraft', draft)
      drafts.value = []
      showDraftDialog.value = false
    }

    const handleDiscardDraft = (draftKey) => {
      clearDraft(draftKey)
      drafts.value = drafts.value.filter(d => d.draftKey !== draftKey)
      if (drafts.value.length === 0) {
        showDraftDialog.value = false
      }
    }

    const handleDiscardAllDrafts = () => {
      drafts.value.forEach(d => clearDraft(d.draftKey))
      drafts.value = []
      showDraftDialog.value = false
    }

    onMounted(async () => {
      // 只在当前会话没有剪辑内容时检查历史草稿。
      // 从时间轴等页面携带片段进入时，Vuex 中已经有实时剪辑内容，不能再用旧草稿打断或覆盖它。
      if ((store.state.multiClipper.groups || []).length > 0) return

      const list = await listDrafts()

      // IndexedDB 查询是异步的；查询期间如果有内容导入，也不再弹出恢复提示。
      if ((store.state.multiClipper.groups || []).length > 0) return

      drafts.value = (list || [])
        .filter(d => d.groups && d.groups.length > 0)
        .map(d => ({ ...d, summary: draftSummary(d) }))
      if (drafts.value.length > 0) {
        showDraftDialog.value = true
      }
    })

    // Panel Visibility State
    const showLeftPanel = ref(true)
    const showRightPanel = ref(true)
    // const showHelp = ref(false) // Moved to VideoPlayer.vue

    const showClipPoolHelp = () => {
        if (clipPoolRef.value) {
            clipPoolRef.value.showHelp = true
        }
    }

    const layoutKey = computed(() => `layout-${showLeftPanel.value}-${showRightPanel.value}`)

    const previewSource = computed(() => store.state.multiClipper.previewSource)
    const activePanel = computed(() => store.state.multiClipper.activePanel)
    
    const videoUrl = computed(() => {
        if (!previewSource.value) return null
        if (previewSource.value.urls && previewSource.value.urls.length > 0) {
            return previewSource.value.urls[0]
        }
        return previewSource.value.video_url || previewSource.value.url
    })

    const onTimeUpdate = (time) => {
        videoCurrentTime.value = time
    }

    const handleSeekRequest = (time) => {
        seekRequest.value = { time, ts: Date.now() }
    }

    const handlePauseRequest = () => {
       // console.log('[MultiClipperLayout] handlePauseRequest called')
       isPlaying.value = false // Force state update immediately to stop consumers like ClipPool
       if (videoPlayerRef.value) {
           // Try dedicated method first, fallback to direct ref access if needed (though method is preferred)
           if (typeof videoPlayerRef.value.pauseVideo === 'function') {
               videoPlayerRef.value.pauseVideo()
           } else if (videoPlayerRef.value.videoRef) {
               // Fallback for safety
               videoPlayerRef.value.videoRef.pause()
           }
       }
    }

    const handlePlaySegment = async (segment) => {
        // console.log('[MultiClipperLayout] handlePlaySegment called', segment) 
        
        let targetSegment = { ...segment }
      const { targetPanel, autoPlay, ...persistableSegment } = targetSegment

        // Switch video source if needed
        if (targetSegment.video_url || targetSegment.video_id) {
             // If video_url is missing but we have video_id, fetch it
             if (!targetSegment.video_url && targetSegment.video_id) {
                 // Check if it's the current preview source
                 if (previewSource.value && (previewSource.value.video_id == targetSegment.video_id || previewSource.value.id == targetSegment.video_id)) {
                     // It is current source, try to get URL from it
                     targetSegment.video_url = previewSource.value.video_url || (previewSource.value.urls ? previewSource.value.urls[0] : null)
                 }
             }

            // Only update source if it changed
            const currentVideoId = previewSource.value ? (previewSource.value.video_id || previewSource.value.id) : null
            const targetVideoId = targetSegment.video_id || targetSegment.id
            
            const currentTransId = previewSource.value ? (previewSource.value.transcription_id || currentVideoId) : null
            const targetTransId = targetSegment.transcription_id || targetVideoId

            // Check both video_id and transcription_id for source equality
            // Use loose equality or String cast to handle potential number/string ID mismatch
            const isSameSource = currentVideoId && targetVideoId && String(currentVideoId) === String(targetVideoId) && 
                                 String(currentTransId) === String(targetTransId)

            if (!isSameSource) {
                const newSource = {
                id: persistableSegment.video_id || persistableSegment.id,
                video_id: persistableSegment.video_id || persistableSegment.id,
                transcription_id: persistableSegment.transcription_id || persistableSegment.video_id || persistableSegment.id,
                video_name: persistableSegment.video_name || persistableSegment.title,
                    // Ensure we have at least one url or video_url
                url: persistableSegment.video_url || persistableSegment.url,
                video_url: persistableSegment.video_url || persistableSegment.url,
                urls: (persistableSegment.urls && persistableSegment.urls.length > 0) ? persistableSegment.urls : (persistableSegment.video_url ? [persistableSegment.video_url] : []),
                    // We might want to keep other properties if available
                ...persistableSegment,
                    _updateTs: Date.now() // Force update for watchers
                }
                
                store.dispatch('multiClipper/selectPreview', newSource)
            }
        }

        // Seek to start time if available
        if (segment.start_time !== undefined && segment.start_time !== null) {
            seekRequest.value = { 
                time: Number(segment.start_time) / 1000, 
                ts: Date.now(),
                playAfterSeek: !!segment.autoPlay
            }
        }
        
        activatePanel(segment.targetPanel || 'pool')
    }
    
    const activatePanel = (panel) => {
        store.dispatch('multiClipper/setActivePanel', panel)
    }

    return {
        videoUrl,
        videoCurrentTime,
        seekRequest,
        activePanel,
        showLeftPanel,
        showRightPanel,
        // showHelp,
        layoutKey,
        onTimeUpdate,
        handleSeekRequest,
        handlePauseRequest,
        handlePlaySegment,
        activatePanel,
        videoPlayerRef,
        materialSearchRef,
        clipPoolRef,
        showClipPoolHelp,
        isPlaying,
        showDraftDialog,
        drafts,
        handleRestoreDraft,
        handleDiscardDraft,
        handleDiscardAllDrafts
    }
  }
}
</script>

<style scoped>
.multi-clipper-layout {
  height: calc(100vh - 85px);
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.layout-toolbar {
  height: 30px;
  background-color: #f5f5f5;
  border-bottom: 1px solid #ddd;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 10px;
}

.toggle-btn {
  background: none;
  border: none;
  font-size: 12px;
  cursor: pointer;
  color: #666;
  display: flex;
  align-items: center;
  gap: 4px;
}
.toggle-btn:hover {
  color: #1976D2;
}

/* Help Modal Styles moved to VideoPlayer.vue */
.panel {
  height: 100%;
  width: 100%;
  padding: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

.panel-header {
  height: 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 8px;
  background-color: #eee;
  border-bottom: 1px solid #ddd;
}

.panel-title {
  font-size: 13px;
  font-weight: 500;
  color: #333;
}

.help-icon-trigger {
  cursor: pointer;
  font-size: 16px;
  color: #999;
  transition: color 0.2s;
  display: flex;
  align-items: center;
}

.help-icon-trigger:hover {
  color: #666;
}

.icon-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  border-radius: 4px;
}
.icon-btn:hover {
  background-color: #ddd;
}

.left-panel {
  background-color: #f9f9f9;
}
/* Adjust padding for inner content if needed, previously it was on .panel */
.left-panel > :not(.panel-header) {
    padding: 10px;
    flex: 1;
    overflow: auto;
}

.center-panel {
  background-color: #fff;
  padding: 0; 
}

.video-section {
  height: 100%;
  width: 100%;
  background: #000;
  border-bottom: 1px solid #333;
}

.source-section {
  height: 100%;
  width: 100%;
  overflow: hidden;
  position: relative;
  border: 4px solid transparent; 
  transition: border-color 0.2s;
}

.right-panel {
  background-color: #f9f9f9;
  border-right: none;
  border: 4px solid transparent;
  transition: border-color 0.2s;
}

.right-panel > :not(.panel-header) {
    /* padding: 10px; Remove padding from container, let child handle it if needed or add here */
    flex: 1;
    overflow: hidden; /* ClipPool handles its own scroll usually */
    display: flex;
    flex-direction: column;
}

/* Active Panel Styling */
.active-panel {
  border-color: #FF5722; /* Deep Orange */
}

/* Splitpanes custom styling overrides */
:deep(.splitpanes__splitter) {
  background-color: #eee;
  border-left: 1px solid #ddd;
  width: 6px; /* slightly wider for easier grab */
}

:deep(.splitpanes__splitter:hover) {
  background-color: #ccc;
}
</style>
